import {isUndefined} from '#lib/utils/undefined.js';

/**
 * Lodash implementation of `get` without support for default values and path
 * must be an array
 * @param {Object} object The object to query.
 * @param {Array} path The path of the property to get.
 * @returns {*} Returns the resolved value.
 */
export function get(object, path) {
    let index = 0;
    const max = path.indexOf(undefined);

    while (object != null && index < max) {
        object = object[path[index++]];
    }

    return index && index === max ? object : undefined;
}

export function hasKey(object, property) {
    return Object.prototype.hasOwnProperty.call(object, property);
}

export function getPropertyIfDefined(object, property) {
    try {
        const hasProperty = hasKey(object, property);
        if (hasProperty) return object[property];
    } catch (error) {
        console.error(error);
    }
    return undefined;
}

export function isObject(variable) {
    return typeof variable === 'object';
}

export function objPartition(object, keys) {
    return Object.entries(object).reduce(
        (result, [key, value]) => (
            (result[keys.includes(key) ? 0 : 1][key] = value), result
        ),
        [{}, {}]
    );
}

export function pluck(object, keys) {
    let result = {};
    if (isUndefined(object)) return undefined;
    Object.entries(object).forEach(([key, value]) => {
        if (keys.includes(key)) result[key] = value;
    });
    return result;
}

export function omit(object, keys) {
    let result = {};
    Object.entries(object).forEach(([key, value]) => {
        if (keys.includes(key)) return;
        result[key] = value;
    });
    return result;
}

export function isObjEmpty(object) {
    if (!object || !isObject(object)) return undefined;
    return Object.keys(object).length === 0;
}

export function hasProperties(object) {
    if (!object) return undefined;
    return !isObjEmpty(object);
}

/*!
 * Deep merge two or more objects or arrays.
 * (c) 2023 Chris Ferdinandi, MIT License, https://gomakethings.com
 * @param   {*} ...objs  The arrays or objects to merge
 * @returns {*}          The merged arrays or objects
 */
export function deepMerge(...objs) {
    /**
     * Get the object type
     * @param  {*}       obj The object
     * @return {String}      The object type
     */
    function getType(obj) {
        return Object.prototype.toString.call(obj).slice(8, -1).toLowerCase();
    }

    /**
     * Deep merge two objects
     * @return {Object}
     */
    function mergeObj(clone, obj) {
        for (let [key, value] of Object.entries(obj)) {
            let type = getType(value);
            if (
                clone[key] !== undefined &&
                getType(clone[key]) === type &&
                ['array', 'object'].includes(type)
            ) {
                clone[key] = deepMerge(clone[key], value);
            } else {
                clone[key] = structuredClone(value);
            }
        }
    }

    // Create a clone of the first item in the objs array
    let clone = structuredClone(objs.shift());

    // Loop through each item
    for (let obj of objs) {
        // Get the object type
        let type = getType(obj);

        // If the current item isn't the same type as the clone, replace it
        if (getType(clone) !== type) {
            clone = structuredClone(obj);
            continue;
        }
        // Otherwise, merge
        if (type === 'array') {
            clone = [...clone, ...structuredClone(obj)];
        } else if (type === 'object') {
            mergeObj(clone, obj);
        } else {
            clone = obj;
        }
    }
    return clone;
}
